import React from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { Button, FormCheck } from 'react-bootstrap'
import linkFromContentStack from 'src/deps/LinkFromContantStack'
import { getProcessingDateDetails, capitalizeFirstLetters } from 'src/deps'
import {
  multipleSubscriptionContainer,
  cartContainer,
  storeContainer,
} from 'src/models'
import { overlayState } from 'src/views/components'
import {
  i18nTranslate,
  IS_BROWSER,
  isINMarket,
  getLocaleCodeFromUrl,
} from 'src/utils'
import { getMaxGrandTotalFromResponse } from 'src/utils/multipleSubscriptionUtils'
import { checkIsNativeApp } from 'src/utils/reactNativeAppUtils'
import {
  isEquinoxPOSite,
  isEquinoxMysite,
  getPoSponsorId,
  getMysiteSponsor,
} from 'src/utils/mySiteUtils'
import './styles.scss'

@observer
class SubscriptionAgreementSection extends React.Component {
  @observable isPaymentAgreementChecked = false
  @observable isTermsAgreementChecked = false

  constructor(props) {
    super(props)
  }

  /**
   * Builds eMandate payload based on cart subscription items, similar to updateCartItemsToSubscription.
   * Returns an object with items array and landingUrl at the root level.
   */
  buildEMandatePayload = () => {
    const landingUrl = IS_BROWSER ? window.location.href + `?type=0` : ''
    const subscriptionItemsInCart =
      multipleSubscriptionContainer.getSubscriptionItemsInCart() || []

    // Transform cart items similar to getTransformedCartProducts
    const transformedItems = subscriptionItemsInCart.map(cartItem => {
      const {
        quantity = 1,
        subscription = {},
        properties = {},
        skus = [],
        itemId = '',
      } = cartItem || {}

      const { category, categoryId, scanCardDetails = '' } = properties || {}
      const mainProductData =
        multipleSubscriptionContainer.getProductSkuDetails(skus) || []

      return {
        quantity,
        itemId,
        skus: mainProductData,
        isSubscription: true,
        subscription,
        properties: {
          categoryId,
          category,
          isOneTimeOrderItem: false,
          ...subscription,
          scanCardDetails,
        },
      }
    })

    return {
      items: transformedItems,
      landingUrl,
    }
  }

  isAuthorizationLimitBreached = () => {
    const authorizedLimit = Number(
      multipleSubscriptionContainer.getMaxGrandTotalFromResponse()
    )
    const existingSubscriptionGrandTotal = Number(
      multipleSubscriptionContainer.getSelectedSubscriptionGrandTotal()
    )
    const subscriptionSubTotal = Number(
      cartContainer.cartResponse?.properties?.subsctiptionItemsTotal || 0
    )

    const isLimitBreached =
      authorizedLimit > 0 &&
      existingSubscriptionGrandTotal + subscriptionSubTotal > authorizedLimit

    return isINMarket() && isLimitBreached
  }

  handleEMandateFlow = async (errorResponse = {}) => {
    // Only proceed with eMandate flow for India market
    if (!isINMarket()) {
      overlayState.hideLoader()
      return
    }

    const subscriptionId =
      multipleSubscriptionContainer?.selectedSubscriptionGroupDetails?.id || ''

    if (!subscriptionId) {
      overlayState.hideLoader()
      return
    }

    try {
      const payload = this.buildEMandatePayload()
      const emandateResponse =
        await multipleSubscriptionContainer.createSubscriptionEMandate(
          subscriptionId,
          payload
        )

      // If eMandate call fails, abort further processing
      if (!multipleSubscriptionContainer.isSuccessResponse(emandateResponse)) {
        overlayState.hideLoader()
        return
      }

      const redirectUrl = emandateResponse?.redirectUrl

      if (redirectUrl && IS_BROWSER) {
        // Extract gpsOrderId from redirectUrl
        const gpsOrderId = redirectUrl.split('/').pop()

        // Store subscriptionId and gpsOrderId in sessionStorage for status verification
        sessionStorage.setItem('emandate_subscriptionId', subscriptionId)
        sessionStorage.setItem('emandate_gpsOrderId', gpsOrderId)

        // Redirect user to payment gateway (loader stays on during redirect)
        window.location.href = redirectUrl
        return
      }
      overlayState.hideLoader()
    } catch (error) {
      overlayState.hideLoader()
      console.error('eMandate flow error:', error)
    }
  }

  handleSubscriptionContinue = async () => {
    // Always call updateCartItemsToSubscription first
    await this.props.handleAddToSubscription(this.handleEMandateFlow)
  }

  handlePaymentAgreementToggle = () => {
    this.isPaymentAgreementChecked = !this.isPaymentAgreementChecked
  }

  handleTermsAgreementToggle = () => {
    this.isTermsAgreementChecked = !this.isTermsAgreementChecked
  }

  navigateToTermsAndConditions = async e => {
    e.preventDefault()
    if (IS_BROWSER) {
      if (linkFromContentStack?.termAndConditionLink) {
        window.open(linkFromContentStack?.termAndConditionLink)
      } else {
        const termAndConditionLink =
          await linkFromContentStack.fetchTermsConditionsLink()
        window.open(termAndConditionLink)
      }
    }
  }

  renderAddToSubscriptionButton = () => {
    const isToEnableSubmitOrder =
      this.isPaymentAgreementChecked && this.isTermsAgreementChecked
    const subscriptionName =
      multipleSubscriptionContainer?.selectedSubscriptionGroupDetails?.name ||
      ''

    const modifiedSubscriptionName = capitalizeFirstLetters(subscriptionName)
    const continueText = i18nTranslate(
      'cartCreateNewSubscriptionComponent.addToSubscription.addToSubscriptionButtonText',
      'Add to %subscription_name%',
      { isNextGenTranslations: true }
    ).replace('%subscription_name%', modifiedSubscriptionName)

    const buttonLabel = continueText

    return (
      <Button
        className="add-subscription-continue-button w-100"
        disabled={
          !isToEnableSubmitOrder ||
          multipleSubscriptionContainer.isAddToSubscriptionApiInProgress
        }
        data-testid="qa-add-subscription-continue-button"
        onClick={this.handleSubscriptionContinue}>
        {buttonLabel}
      </Button>
    )
  }

  renderAgreementCheckboxes = () => {
    const agreementCheckboxText = i18nTranslate(
      'subscriptionModal.agreementCheckboxText',
      'I agree the credit card saved on my Subscription will be charged each processing date'
    )

    const termsAndConditionCheckboxText = i18nTranslate(
      'subscriptionModal.termsAndConditionCheckboxText',
      'I have read and agree to the'
    )

    const termsAndConditionCheckboxLink = i18nTranslate(
      'subscriptionModal.termsAndConditionLink',
      'Subscription Terms and Conditions'
    )

    return (
      <div className="subscription-agreement-section-wrapper">
        <FormCheck
          className="agreement-checkbox-wrapper"
          data-testid="qa-subscription-payment-checkbox">
          <FormCheck.Input
            checked={this.isPaymentAgreementChecked}
            onChange={this.handlePaymentAgreementToggle}
            type="checkbox"
            className="payment-confirmation-checkbox"
            id="subscription-payment-info-checkbox"
          />
          <FormCheck.Label
            className="payment-confirmation-label"
            htmlFor="subscription-payment-info-checkbox">
            {agreementCheckboxText}
          </FormCheck.Label>
        </FormCheck>
        <FormCheck
          className="terms-checkbox-wrapper"
          data-testid="qa-subscription-terms-checkbox">
          <FormCheck.Input
            checked={this.isTermsAgreementChecked}
            onChange={this.handleTermsAgreementToggle}
            type="checkbox"
            className="terms-agreement-checkbox"
            id="subscription-terms-info-checkbox"
          />
          <FormCheck.Label
            className="terms-agreement-label"
            htmlFor="subscription-terms-info-checkbox">
            {termsAndConditionCheckboxText}{' '}
            <span
              className="terms-and-conditions-link-wrapper"
              role="link"
              tabIndex="0"
              onClick={e => this.navigateToTermsAndConditions(e)}
              onKeyPress={e => this.navigateToTermsAndConditions(e)}>
              <u>{termsAndConditionCheckboxLink}</u>
            </span>
          </FormCheck.Label>
        </FormCheck>
      </div>
    )
  }

  renderSubscriptionInfo = () => {
    const itemTotalText = i18nTranslate(
      'subscriptionModal.itemTotalText',
      'Subtotal does not include shipping, taxes or items already on subscription (see Subscription Dashboard for monthly totals).'
    )
    const subscriptionProcessText = i18nTranslate(
      'subscriptionModal.subscriptionProcessText',
      'You may change or cancel your Subscriptions at any time through your Subscription Dashboard (processing times apply).'
    )

    const { processingDay, lastOrderProcessedDate, nextOrderCreationDate } =
      multipleSubscriptionContainer.selectedSubscriptionGroupDetails
    const dateDetails = getProcessingDateDetails({
      processingDay,
      lastOrderProcessedDate,
      nextOrderCreationDate,
    })
    const { subscriptionItemsCountInCart = 0 } = this.props

    const isToShowMultipleItemsText = subscriptionItemsCountInCart > 1

    const nextOrderProcessingFullDate = dateDetails.orderProcessDate

    const nextOrderProcessingDate = dateDetails.orderProcessDay

    return (
      <div
        className="subscription-info-wrapper"
        data-testid="qa-subscription-agreement-section">
        <p className="subscription-processing-info">
          {isToShowMultipleItemsText
            ? i18nTranslate(
                'subscriptionModal.itemsProcessText',
                'These items will process'
              )
            : i18nTranslate(
                'subscriptionModal.itemProcessText',
                'This item will process'
              )}{' '}
          {nextOrderProcessingFullDate}{' '}
          {i18nTranslate('subscriptionModal.andOnText', 'and on the')}{' '}
          {nextOrderProcessingDate}{' '}
          {i18nTranslate(
            'subscriptionModal.futureDayText',
            'day of future months, based on chosen frequency.'
          )}
        </p>
        <p>{itemTotalText}</p>
        <p>{subscriptionProcessText}</p>
      </div>
    )
  }

  render() {
    return (
      <div className="agreement-section-wrapper">
        <div className="multi-subscription-checkbox-wrapper">
          {this.renderSubscriptionInfo()}
          {this.renderAgreementCheckboxes()}
        </div>
        {this.renderAddToSubscriptionButton()}
      </div>
    )
  }
}

export default SubscriptionAgreementSection
export { SubscriptionAgreementSection }
